/*
 * <insurance-coverage> — embedded insurance eligibility view.
 * Shopper selects a shipping state; we reveal the carriers accepted in that
 * state (client-side filter over hardcoded <li data-states> — no fetch), plus
 * two CTAs. Config comes from data-* attributes on the element.
 *
 * Dropdown plumbing mirrors the modal (snippets/product-insurance-modal-hss.liquid);
 * CTA id-appending reuses window.SDFormTracking (assets/sd-form-tracking.js).
 *
 * The italic card note under the divider is a Paragraph block whose rich text
 * may contain `{state}` ("We accept the above insurance providers in {state}.").
 * Each one is wrapped in [data-card-note] by the section; we cache the wrapper's
 * original innerHTML once and re-render it on every selection. Substituting into
 * innerHTML is safe here: the template is merchant rich text (already trusted as
 * HTML) and the replacement comes from the hardcoded US-state list, not input.
 *
 * Two representations of the same selection, deliberately:
 *   selectedState     full name ("California") — what the carrier filter and the
 *                     "Accepted in {state}" heading are keyed on.
 *   selectedStateCode 2-letter code ("CA")     — what Bridge expects, appended to
 *                     the "Continue with insurance" URL so the coverage-verification
 *                     page can run its /route call without asking again.
 */
(function () {
  'use strict';

  var MOBILE_BREAKPOINT = 749;

  function normalize(value) {
    return (value || '').trim().toLowerCase();
  }

  class InsuranceCoverage extends HTMLElement {
    constructor() {
      super();
      this.selectedState = '';
      this.selectedStateCode = '';
    }

    connectedCallback() {
      this.cardTitleTemplate = this.getAttribute('data-card-title') || 'Accepted in {state}';
      this.noCarriersTemplate = this.getAttribute('data-no-carriers-text') || '';
      this.newPatientUrl = this.getAttribute('data-new-patient-url') || '';
      /* Blank switches the state hand-off off entirely. */
      this.stateParam = this.getAttribute('data-state-param') || '';
      this.cashCartUrl = this.getAttribute('data-cash-cart-url') || '';

      this.dropdown = this.querySelector('[data-state-dropdown]');
      this.results = this.querySelector('[data-results]');
      this.cardHeading = this.querySelector('[data-card-heading]');
      this.carriers = Array.prototype.slice.call(this.querySelectorAll('[data-states]'));
      this.carrierList = this.querySelector('[data-carriers]');
      this.emptyEl = this.querySelector('[data-empty]');
      /* Cache each card note's original markup as its {state} template. */
      this.cardNotes = Array.prototype.slice.call(this.querySelectorAll('[data-card-note]')).map(function (el) {
        return { el: el, template: el.innerHTML };
      });
      this.getStartedBtn = this.querySelector('[data-get-started]');
      this.continueBtn = this.querySelector('[data-continue-without]');

      if (this.dropdown) this.initDropdown(this.dropdown);

      this.handleOutsideClick = function (e) {
        if (!e.target.closest('.sd-insurance-dropdown')) this.closeDropdown(this.dropdown);
      }.bind(this);
      document.addEventListener('click', this.handleOutsideClick);

      if (this.getStartedBtn) {
        this.getStartedBtn.addEventListener('click', this.onGetStarted.bind(this));
      }
      if (this.continueBtn) {
        this.continueBtn.addEventListener('click', this.onContinueWithout.bind(this));
      }
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleOutsideClick);
    }

    isMobile() {
      return window.innerWidth <= MOBILE_BREAKPOINT;
    }

    /* ---- Dropdown ---- */

    initDropdown(root) {
      var self = this;
      var trigger = root.querySelector('[data-dropdown-trigger]');
      var options = Array.prototype.slice.call(root.querySelectorAll('.sd-insurance-dropdown-option'));

      trigger.addEventListener('click', function (e) {
        e.stopPropagation();
        if (root.classList.contains('is-open')) self.closeDropdown(root);
        else self.openDropdown(root);
      });
      trigger.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          self.closeDropdown(root);
        } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          self.openDropdown(root);
          self.focusOption(options, e.key === 'ArrowUp' ? options.length - 1 : 0);
        }
      });

      options.forEach(function (opt, index) {
        opt.addEventListener('click', function () {
          self.selectState(root, opt);
        });
        opt.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            self.selectState(root, opt);
          } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            self.focusOption(options, index + 1);
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            self.focusOption(options, index - 1);
          } else if (e.key === 'Escape') {
            self.closeDropdown(root);
            trigger.focus();
          }
        });
      });
    }

    /* Move focus to the option at `index`, wrapping and skipping hidden ones. */
    focusOption(options, index) {
      var count = options.length;
      if (!count) return;
      var i = ((index % count) + count) % count;
      for (var step = 0; step < count; step += 1) {
        var opt = options[i];
        if (!opt.hidden && !opt.classList.contains('is-hidden')) {
          opt.focus();
          return;
        }
        i = ((i + (index < 0 ? -1 : 1)) % count + count) % count;
      }
    }

    openDropdown(root) {
      root.classList.add('is-open');
      root.querySelector('[data-dropdown-trigger]').setAttribute('aria-expanded', 'true');
      root.querySelector('[data-dropdown-list]').hidden = false;
    }

    closeDropdown(root) {
      if (!root) return;
      root.classList.remove('is-open');
      root.querySelector('[data-dropdown-trigger]').setAttribute('aria-expanded', 'false');
      root.querySelector('[data-dropdown-list]').hidden = true;
    }

    selectState(root, opt) {
      var value = opt.getAttribute('data-value');
      var valueEl = root.querySelector('[data-dropdown-value]');
      valueEl.textContent = opt.querySelector('.sd-insurance-dropdown-option-title').textContent;
      valueEl.classList.remove('is-placeholder');
      root.querySelectorAll('.sd-insurance-dropdown-option').forEach(function (o) {
        o.setAttribute('aria-selected', o === opt ? 'true' : 'false');
      });
      this.closeDropdown(root);

      this.selectedState = value;
      this.selectedStateCode = opt.getAttribute('data-code') || '';
      this.renderResults();
      this.pushDataLayer({ event_action: 'state_selected', shipping_state: value });
    }

    /* ---- Results ---- */

    renderResults() {
      var state = this.selectedState;
      var stateKey = normalize(state);

      if (this.cardHeading) {
        this.cardHeading.textContent = this.cardTitleTemplate.replace(/\{state\}/gi, state);
      }

      if (this.cardNotes) {
        this.cardNotes.forEach(function (note) {
          note.el.innerHTML = note.template.replace(/\{state\}/gi, state);
        });
      }

      var visibleCount = 0;
      this.carriers.forEach(function (li) {
        var raw = li.getAttribute('data-states') || '';
        var match = raw === 'ALL' || raw.split(',').some(function (s) {
          return normalize(s) === stateKey;
        });
        li.hidden = !match;
        if (match) visibleCount += 1;
      });

      var empty = visibleCount === 0;
      if (this.carrierList) this.carrierList.hidden = empty;
      if (this.emptyEl) {
        this.emptyEl.hidden = !empty;
        if (empty) this.emptyEl.textContent = this.noCarriersTemplate.replace(/\{state\}/gi, state);
      }

      if (this.results) this.results.hidden = false;
    }

    /* ---- CTAs ---- */

    onGetStarted(e) {
      e.preventDefault();
      if (!this.newPatientUrl) {
        this.pushDataLayer({
          event_action: 'get_started_click',
          shipping_state: this.selectedState,
          redirect_url: this.newPatientUrl
        });
        return;
      }
      /* Carry the 2-letter code forward. The destination (coverage-verification)
         needs it for its Bridge /route call; without it the shopper would have to
         pick the state a second time on the very next screen. */
      var url = this.newPatientUrl;
      if (this.stateParam && this.selectedStateCode) {
        var sep = url.indexOf('?') > -1 ? '&' : '?';
        url += sep + encodeURIComponent(this.stateParam) + '=' + encodeURIComponent(this.selectedStateCode);
      }
      this.pushDataLayer({
        event_action: 'get_started_click',
        shipping_state: this.selectedState,
        redirect_url: url
      });
      if (window.SDFormTracking && window.SDFormTracking.appendIds) {
        window.SDFormTracking.appendIds(url, function (finalUrl) {
          window.location.href = finalUrl;
        });
      } else {
        window.location.href = url;
      }
    }

    onContinueWithout(e) {
      e.preventDefault();
      this.pushDataLayer({
        event_action: 'continue_without_insurance_click',
        shipping_state: this.selectedState
      });
      window.location.href = this.cashUrlWithPartner();
    }

    /* Append the partner-referral id as a cart attribute, matching the modal. */
    cashUrlWithPartner() {
      if (!this.cashCartUrl) return '#';
      var pid = (window.SDFormTracking && window.SDFormTracking.getPartnerId)
        ? window.SDFormTracking.getPartnerId() : '';
      if (!pid) return this.cashCartUrl;
      var sep = this.cashCartUrl.indexOf('?') > -1 ? '&' : '?';
      return this.cashCartUrl + sep + 'attributes[partnerID]=' + encodeURIComponent(pid);
    }

    /* ---- Analytics ---- */

    pushDataLayer(params) {
      window.dataLayer = window.dataLayer || [];
      var payload = { event: 'insurance_modal', device_type: this.isMobile() ? 'mobile' : 'desktop' };
      Object.keys(params).forEach(function (k) { payload[k] = params[k]; });
      window.dataLayer.push(payload);
    }
  }

  if (!customElements.get('insurance-coverage')) {
    customElements.define('insurance-coverage', InsuranceCoverage);
  }
})();
